import { getUsername } from './user';

// Tracking fields for various casting if needed in sort function below
const date_fields = ['expiration_date'];

/**
 * Sorts an array of data, according to a specified field.
 *
 * @param {String} order - The order of sorting, either "asc" or "desc".
 * @param {String} field - The name of the field to sort by.
 * @param {Array} data - The array containing objects to be sorted.
 *
 * @return {Array} The sorted array.
 */
export function sort(order, field, data) {
  // Keep track of fields that are dates and sortable
  let compare_dates = false;
  // If we have a date field, set the date to be dates
  if (date_fields.includes(field)) {
    compare_dates = true;
  }
  // Assume we are not sorting strings to start
  let compare_letters = false;
  let includes_accents = false;
  const regexPattern = /[àèìòùáéíóúýâêîôûãñõäëïöüÿÀÈÌÒÙÁÉÍÓÚÝÂÊÎÔÛÃÑÕÄËÏÖÜŸ]/;
  data.forEach(el => {
    if (typeof el[field] === 'string' && isNaN(el[field])) {
      compare_letters = true;
    }
    if (regexPattern.test(el[field])) {
      includes_accents = true;
    }
  });
  return data.sort((a, b) => {
    let comparison = 0;

    // check for null values first
    if (a[field] == null && b[field] == null) {
      comparison = 0;
    } else if (a[field] == null && b[field] !== null) {
      comparison = -1;
    } else if (a[field] !== null && b[field] == null) {
      comparison = 1;
    } else {
      // comparison if values are not null and dates
      if (compare_dates) {
        if (new Date(a[field]) > new Date(b[field])) {
          comparison = 1;
        } else if (new Date(b[field]) > new Date(a[field])) {
          comparison = -1;
        }
      }
      // comparison is between strings with accent letters
      else if (includes_accents) {
        comparison = a[field].localeCompare(b[field], 'en', {
          sensitivity: 'accent',
        });
      }
      // comparison is between strings which may be different casings
      else if (compare_letters) {
        if (a[field].toLowerCase() > b[field].toLowerCase()) {
          comparison = 1;
        } else if (b[field].toLowerCase() > a[field].toLowerCase()) {
          comparison = -1;
        }
      }
      // comparison if both values are not null
      else if (Number(a[field]) > Number(b[field])) {
        comparison = 1;
      } else if (Number(b[field]) > Number(a[field])) {
        comparison = -1;
      }
    }

    return order === 'asc' ? comparison * 1 : comparison * -1;
  });
}

/**
 * Sorts a users array, assumes you have a first or last name.
 *
 * @param {*} users - The users array to be sorted
 * @param {*} users_store - The users store is needed for details to sort by
 * @param {*} prioritizeAssigneeId - The auxo_user_id of the user to prioritize at the top of the list, if present
 * @returns The sorted array with returned details from the users store
 */
export const sortUsers = (users, users_store, prioritizeAssigneeId) => {
  if (users == undefined) return [];
  const stored_users = users
    .map(user => {
      if (user.auxo_user_id) return users_store.getUserById(user.auxo_user_id);
      else if (user.username)
        return users_store.getUserByUsername(user.username);
      else return users_store.getUserByUsername(getUsername(user.email));
    })
    .filter(Boolean);
  return stored_users.sort((a, b) => {
    if (prioritizeAssigneeId) {
      const isA = a.auxo_user_id == prioritizeAssigneeId;
      const isB = b.auxo_user_id == prioritizeAssigneeId;

      if (isA && !isB) return -1;
      if (!isA && isB) return 1;
    }
    const nameA = [a.first_name, a.last_name].filter(e => e).join(' ');
    const nameB = [b.first_name, b.last_name].filter(e => e).join(' ');
    return nameA.localeCompare(nameB);
  });
};

/**
 * Sorts array of roles to follow correct ordering: ['REP', 'SUP', 'MGR', 'ADM']
 * @param {*} roleArr array of DB valued roles ex: ['REP', 'SUP']
 * @returns correctly sorted array based on access level
 */
export function sortUserRoles(roles) {
  const roles_correct_order = ['REP', 'SUP', 'MGR', 'ADM'];
  const orderMap = new Map(
    roles_correct_order.map((role, index) => [role, index])
  );
  return roles.sort((a, b) => orderMap.get(a) - orderMap.get(b));
}

/**
 * Returns dropdown options organized by a nested array of objects.
 * @param {Array} arr - The array of option objects to be organized
 * @returns {Array} The array of organized option objects
 */
export function sort_options_alphabetically(arr) {
  arr.sort((a, b) => a.level - b.level);
  const organized = [];
  arr.forEach(el => {
    if (el.level === 0) {
      if (el.child_ids) {
        // used for phase/status/reason
        organized.push({ ...el, children: [] });
      } else {
        // used for payer/plan — keep children attached by
        // payerPlanOptionsComputed; only default to [] when the caller
        // didn't supply any, otherwise expandable parents render empty.
        organized.push({ ...el, children: el.children ?? [] });
      }
    } else if (el.level === 1 && el.parent_ids) {
      // && el.parent_ids check is so this only executes for phase/status/reason
      organized.map(item => {
        if (item.value.rsp_id === el.parent_ids[0]) {
          let children = arr.filter(rsp =>
            el.child_ids.includes(rsp.value.rsp_id)
          );
          children.shift();
          item.children.push({ ...el, children: [...children] });
        }
      });
    }
  });

  const sort_logic = (a, b) => a.label.localeCompare(b.label);

  organized.sort(sort_logic);

  organized.forEach(el => {
    if (el.children.length) {
      el.children.sort(sort_logic);

      el.children.forEach(child => {
        if (child.children) child.children.sort(sort_logic);
      });
    }
  });

  return organized;
}

/**
 * Returns the array of options destructured into a one dimmentional array.
 * @param {Array} arr - The array of nested option objects to be destructured.
 * @returns {string} The destructured array of option objects.
 */
export function destructure_options(arr) {
  const result = [];

  arr.forEach(el => {
    result.push(el);
    if (el.children.length) {
      el.children.forEach(child => {
        result.push(child);
        if (child.children.length) {
          child.children.forEach(inner_child => {
            result.push(inner_child);
          });
        }
      });
    }
  });

  return result;
}

/**
 * a JS method to move an object to a specific index in an array,
 * and then adjust the rank attribute of elements indexed before or after the targeted
 * position depending on if they moved up in index, or down in index.
 *
 * @param {*} arr
 * @param {*} objToMove
 * @param {*} targetIndex
 * @returns
 */
export function moveAndAdjustRank(arr, objToMove, targetIndex) {
  const originalIndex = arr.indexOf(objToMove);

  if (originalIndex === -1) {
    throw new Error('Object not found in array.');
  }

  const targetRank = arr[targetIndex].rank;

  arr.splice(originalIndex, 1);
  arr.splice(targetIndex, 0, objToMove);

  objToMove.rank = targetRank;

  if (originalIndex < targetIndex) {
    for (let i = originalIndex; i < targetIndex; i++) {
      arr[i].rank--;
    }
  } else {
    for (let i = targetIndex + 1; i <= originalIndex; i++) {
      arr[i].rank++;
    }
  }

  return arr;
}
